import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Card, Dropdown, Popover, Skeleton, Typography } from 'antd'
import { Icon } from '@iconify/react'
import { useSelector } from 'react-redux'

import HomeCardHeader from './HomeCardHeader'
import styles from './ProjectsCard.module.css'
import shellStyles from './HomeCardShell.module.css'
import plStyles from '../../../layouts/navigation/projectsList.module.css'
import { harmonicIconToneForBackground } from '../../../helpers/harmonicIconColor'
import { truncateWithEllipsis } from '../../../helpers/utils'
import { loginState } from '../../login/login.slice'
import { projectsState } from '../../projects/projects.slice'
import { PRIVATE_ROUTES } from '../../../routes'
import { DEFAULT_COLOR_PALETTE } from '../../../constants/projects'
import ProjectColorIconFlyout from '../../projects/components/ProjectColorIconFlyout'
import { showActionNotification } from '../../../components/notification'

const { Text } = Typography

const copyTextToClipboard = async (text) => {
    try {
        if (!text) return false
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(text)
            return true
        }
    } catch {
        // fall through
    }
    try {
        const el = document.createElement('textarea')
        el.value = String(text)
        el.setAttribute('readonly', '')
        el.style.position = 'fixed'
        el.style.top = '-1000px'
        document.body.appendChild(el)
        el.select()
        const ok = document.execCommand('copy')
        document.body.removeChild(el)
        return ok
    } catch {
        return false
    }
}

const PROJECT_TILE_SKELETON_COUNT = 6

const SKELETON_NAME_WIDTHS_PCT = [72, 88, 65, 80, 76, 90]

const ProjectTileSkeleton = ({ rowIndex }) => (
    <div
        className={`${styles.projectTile} ${styles.projectTileSkeleton}`}
        aria-hidden
    >
        <div className={styles.projectTileLead}>
            <div className={styles.skeletonProjectIconWrap}>
                <Skeleton.Avatar active size={48} shape="square" />
            </div>
            <div className={styles.projectMeta}>
                <div
                    className={styles.skeletonProjectNameWrap}
                    style={{
                        width: `${SKELETON_NAME_WIDTHS_PCT[rowIndex % SKELETON_NAME_WIDTHS_PCT.length]}%`,
                    }}
                >
                    <Skeleton.Input active size="small" block />
                </div>
            </div>
        </div>
        <div className={styles.projectTileMenuAnchor}>
            <Skeleton.Button
                active
                size="small"
                shape="default"
                className={styles.skeletonProjectMenuBtn}
            />
        </div>
    </div>
)

const CreateProjectTileSkeleton = () => (
    <div
        className={`${styles.createProjectTile} ${styles.createProjectTileSkeleton}`}
        aria-hidden
    >
        <div className={styles.skeletonCreateIconWrap}>
            <Skeleton.Avatar active size={48} shape="square" />
        </div>
        <div className={styles.skeletonCreateTextWrap}>
            <Skeleton.Input active size="small" block />
        </div>
    </div>
)

const PROJECT_COLOR_FALLBACK = [
    '#8ab4f8',
    '#99f6e4',
    '#d8b4fe',
    '#f9a8d4',
    '#fdba74',
]

/** Max characters before project name is shortened with `...` on the home card. */
const PROJECT_NAME_DISPLAY_MAX_LEN = 25

const getProjectColor = (project = {}, idx = 0) =>
    project?.colorCode ||
    PROJECT_COLOR_FALLBACK[idx % PROJECT_COLOR_FALLBACK.length]

/** Same avatar rules as `projectDetail.js` project header tile. */
const renderProjectIconInner = (project, idx, styles) => {
    const tileColor = getProjectColor(project, idx)
    const iconTone = harmonicIconToneForBackground(tileColor)

    if (project?.urlType === 'icon' && project?.url) {
        return (
            <Icon
                icon={project.url}
                width={22}
                height={22}
                style={{ color: iconTone }}
            />
        )
    }
    if (project?.urlType === 'upload' && project?.url) {
        return (
            <img
                src={project.url}
                alt={project?.name || ''}
                className={styles.projectIconImage}
            />
        )
    }
    if (!project?.colorCode && project?.projectProfile) {
        return (
            <img
                src={project.projectProfile}
                alt={project?.name || ''}
                className={styles.projectIconImage}
            />
        )
    }
    if (!project?.colorCode) {
        return (
            <Text className={styles.projectIconInitial}>
                {project?.name?.charAt(0)?.toUpperCase()}
            </Text>
        )
    }
    return null
}

const ProjectTileMenu = ({ project, idx, onProjectUpdated }) => {
    const { userProfile } = useSelector(loginState)
    const canSetColor =
        userProfile &&
        (userProfile?.isSuperAdmin || userProfile?.isExistingUser)
    const projectColor = getProjectColor(project, idx)
    const projectLink =
        typeof window !== 'undefined'
            ? `${window.location.origin}${PRIVATE_ROUTES.projects.projectId(
                  project._id
              )}`
            : ''

    const handleCopyLink = async (e) => {
        e.stopPropagation()
        const ok = await copyTextToClipboard(projectLink)
        showActionNotification({
            type: ok ? 'success' : 'error',
            message: ok ? 'Project link copied' : 'Could not copy link',
            notificationKey: 'home-project-link-copied',
        })
    }

    const [actionsOpen, setActionsOpen] = useState(false)

    return (
        <div
            className={styles.projectTileMenuAnchor}
            data-open={actionsOpen ? 'true' : undefined}
        >
            <Dropdown
                trigger={['click']}
                placement="bottomRight"
                open={actionsOpen}
                onOpenChange={setActionsOpen}
                overlayClassName={plStyles.projectContextDropdown}
                popupRender={() => (
                    <div
                        className={plStyles.projectContextMenu}
                        onClick={(ev) => ev.stopPropagation()}
                    >
                        {canSetColor ? (
                            <Popover
                                trigger={['hover']}
                                placement="rightTop"
                                overlayClassName={
                                    plStyles.colorIconPopoverOverlay
                                }
                                getPopupContainer={(triggerNode) =>
                                    triggerNode?.closest?.('.ant-dropdown') ||
                                    document.body
                                }
                                content={
                                    <ProjectColorIconFlyout
                                        projectId={project._id}
                                        palette={DEFAULT_COLOR_PALETTE}
                                        initialColor={project?.colorCode}
                                        onSaved={() => onProjectUpdated?.()}
                                    />
                                }
                                arrow={false}
                            >
                                <button
                                    type="button"
                                    className={`${plStyles.projectContextMenuItem} ${plStyles.projectContextMenuItemRow}`}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <span
                                        className={plStyles.menuColorDot}
                                        style={{
                                            backgroundColor:
                                                project?.colorCode ||
                                                projectColor,
                                        }}
                                        aria-hidden
                                    />
                                    <span
                                        style={{
                                            flex: 1,
                                            textAlign: 'left',
                                        }}
                                    >
                                        Set color & icon
                                    </span>
                                    <Icon
                                        icon="mdi:chevron-right"
                                        width={18}
                                        height={18}
                                        style={{
                                            color: '#94a3b8',
                                            flexShrink: 0,
                                        }}
                                    />
                                </button>
                            </Popover>
                        ) : null}
                        <button
                            type="button"
                            className={plStyles.projectContextMenuItem}
                            onClick={handleCopyLink}
                        >
                            <Icon
                                icon="mdi:link-variant"
                                width={16}
                                height={16}
                                style={{
                                    color: '#757677',
                                    marginRight: 8,
                                    flexShrink: 0,
                                }}
                            />
                            <span>Copy link</span>
                        </button>
                    </div>
                )}
            >
                <button
                    type="button"
                    className={styles.projectTileMenuBtn}
                    aria-label="Project actions"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <Icon icon="mdi:dots-horizontal" width={18} height={18} />
                </button>
            </Dropdown>
        </div>
    )
}

const ProjectsCard = ({
    projects,
    projectsStatus,
    projectsError,
    projectsTotal,
    projectsHasNext,
    onRetry,
    onLoadMore,
    onOpenProjects,
    onOpenProject,
    title = 'Projects',
    limitToFirst,
    onProjectUpdated,
}) => {
    const { userProfile } = useSelector(loginState)
    const { clientModalTotalProjects } = useSelector(projectsState)

    const showCreateProjectTile = useMemo(() => {
        const isSuperOrExistingUser = Boolean(
            userProfile?.isSuperAdmin || userProfile?.isExistingUser
        )
        return (
            isSuperOrExistingUser ||
            (userProfile?.isSupportUser && Number(clientModalTotalProjects) > 5)
        )
    }, [userProfile, clientModalTotalProjects])

    const scrollRef = useRef(null)
    const sentinelRef = useRef(null)
    const data =
        typeof limitToFirst === 'number'
            ? projects.slice(0, limitToFirst)
            : projects

    useEffect(() => {
        if (!sentinelRef.current || !projectsHasNext || !scrollRef.current)
            return
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) onLoadMore?.()
            },
            {
                root: scrollRef.current,
                rootMargin: '140px 0px',
                threshold: 0.1,
            }
        )
        observer.observe(sentinelRef.current)
        return () => observer.disconnect()
    }, [projectsHasNext, onLoadMore, data.length])

    return (
        <Card
            className={`${shellStyles.cardBase} ${styles.projectsCard}`.trim()}
            bordered={false}
        >
            <HomeCardHeader title={title} />
            <div className={shellStyles.cardBody}>
                <div className={shellStyles.scrollArea} ref={scrollRef}>
                    <div className={styles.projectsGrid}>
                        {showCreateProjectTile &&
                            (projectsStatus === 'loading' ? (
                                <CreateProjectTileSkeleton />
                            ) : (
                                <div
                                    className={styles.createProjectTile}
                                    role="button"
                                    tabIndex={0}
                                    onClick={onOpenProjects}
                                >
                                    <div
                                        className={styles.createProjectIconWrap}
                                    >
                                        <Icon icon="mdi:plus" />
                                    </div>
                                    <Text className={styles.createProjectText}>
                                        Create project
                                    </Text>
                                </div>
                            ))}
                        {projectsStatus === 'loading'
                            ? Array.from(
                                  { length: PROJECT_TILE_SKELETON_COUNT },
                                  (_, rowIndex) => (
                                      <ProjectTileSkeleton
                                          key={`project-skeleton-${rowIndex}`}
                                          rowIndex={rowIndex}
                                      />
                                  )
                              )
                            : data.map((project, idx) => {
                                  const projectNameLabel =
                                      project?.name || 'Untitled project'
                                  const projectNameShort = truncateWithEllipsis(
                                      projectNameLabel,
                                      PROJECT_NAME_DISPLAY_MAX_LEN
                                  )
                                  return (
                                      <div
                                          key={
                                              project?._id ||
                                              `${project?.name}-${idx}`
                                          }
                                          className={styles.projectTile}
                                          role="button"
                                          tabIndex={0}
                                          onClick={() =>
                                              onOpenProject?.(project)
                                          }
                                      >
                                          <div
                                              className={styles.projectTileLead}
                                          >
                                              <div
                                                  className={styles.projectIcon}
                                                  style={{
                                                      backgroundColor:
                                                          getProjectColor(
                                                              project,
                                                              idx
                                                          ),
                                                  }}
                                              >
                                                  {renderProjectIconInner(
                                                      project,
                                                      idx,
                                                      styles
                                                  )}
                                              </div>
                                              <div
                                                  className={styles.projectMeta}
                                              >
                                                  <Text
                                                      className={
                                                          styles.projectName
                                                      }
                                                      title={
                                                          projectNameLabel.length >
                                                          PROJECT_NAME_DISPLAY_MAX_LEN
                                                              ? projectNameLabel
                                                              : undefined
                                                      }
                                                  >
                                                      {projectNameShort}
                                                  </Text>
                                                  {project?.isArchived ? (
                                                      <Text
                                                          className={
                                                              styles.projectArchived
                                                          }
                                                      >
                                                          <Icon
                                                              icon="mdi:archive"
                                                              width={12}
                                                          />
                                                          Archived
                                                      </Text>
                                                  ) : null}
                                              </div>
                                          </div>
                                          <ProjectTileMenu
                                              project={project}
                                              idx={idx}
                                              onProjectUpdated={
                                                  onProjectUpdated
                                              }
                                          />
                                      </div>
                                  )
                              })}
                    </div>
                    {projectsStatus === 'error' ? (
                        <div className={shellStyles.errorWrap}>
                            <Text className={shellStyles.errorText}>
                                {projectsError}
                            </Text>
                            <button
                                type="button"
                                className={shellStyles.retryBtn}
                                onClick={onRetry}
                            >
                                Retry
                            </button>
                        </div>
                    ) : null}
                    {projectsStatus !== 'loading' &&
                    data.length === 0 &&
                    projectsStatus !== 'error' ? (
                        <Text className={shellStyles.stateText}>
                            No projects available
                        </Text>
                    ) : null}
                    {projectsStatus === 'loadingMore' ? (
                        <div
                            className={styles.loadMoreLoader}
                            role="status"
                            aria-label="Loading more projects"
                        >
                            <Icon
                                icon="line-md:loading-loop"
                                style={{ fontSize: 22, color: '#6366f1' }}
                                aria-hidden
                            />
                        </div>
                    ) : null}

                    {projectsHasNext && projectsStatus !== 'error' ? (
                        <div
                            ref={sentinelRef}
                            className={shellStyles.sentinel}
                            aria-hidden="true"
                        />
                    ) : null}
                </div>
            </div>
        </Card>
    )
}

export default ProjectsCard
